import { useState, useEffect, useMemo } from 'react'
import { EmptyState } from 'src/components/empty-state'
import { AssistantHint } from 'src/components/assistant-hint'
import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@heroicons/react/24/outline'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { ContentPane } from 'src/components/layout/content-pane'
import { FilterPane } from 'src/components/layout/filter-pane'
import { CustomSelect } from 'src/components/custom-select'
import { SegmentedControl } from 'src/components/segmented-control'
import { SelectOption } from 'src/services/base-model'
import { organizationQueries } from 'src/services/user'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { Spinner } from 'src/components/spinner'
import { PaginationComponent } from 'src/components/pagination'
import { CreateVendorModal } from 'src/components/vendors/create-vendor-modal'
import { EditVendorModal } from 'src/components/vendors/edit-vendor-modal'
import { vendorQueries } from 'src/services/vendors/queries'
import { vendorApi } from 'src/services/vendors/api'
import { Vendor, vendorTypeEnum } from 'src/services/vendors/models'
import { usePagination } from 'src/hooks/use-pagination'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import { VendorItem, VendorListHeader } from 'src/components/vendors/vendor-item'
import { useViewMode } from 'src/hooks/use-view-mode'

type VendorStatusFilter = 'active' | 'inactive' | 'all'

const IS_ACTIVE_BY_STATUS: Record<VendorStatusFilter, boolean | undefined> = {
  active: true,
  inactive: false,
  all: undefined,
}

export const VendorsPage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<VendorStatusFilter>('active')
  const [vendorTypes, setVendorTypes] = useState<SelectOption[]>([])
  const [organizations, setOrganizations] = useState<SelectOption[]>([])
  const [orgSearch, setOrgSearch] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingVendorId, setEditingVendorId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useViewMode('vendors', 'list')
  const { pagination, paginationCallback, goToNextPage, goToPrevPage, goToPage, resetPagination } =
    usePagination()

  useEffect(() => {
    resetPagination()
  }, [search, status, vendorTypes, organizations, resetPagination])

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      vendorApi.update({ id, isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorQueries.all() })
    },
  })

  const { data, isLoading } = useQuery(
    vendorQueries.list({
      filters: {
        search: search || undefined,
        isActive: IS_ACTIVE_BY_STATUS[status],
        vendorType: vendorTypes.map((o) => o.value),
        organization: organizations.map((o) => o.value),
      },
      pagination,
      paginationCallback,
    }),
  )

  const vendors = data?.results || []

  const { data: orgsData, isLoading: loadingOrgs } = useQuery(
    organizationQueries.list({ filters: { search: orgSearch } }),
  )
  const orgOpts = useMemo(
    () => orgsData?.results.map((o) => ({ label: o.name, value: o.id })) || [],
    [orgsData?.results],
  )
  const vendorTypeOpts = useMemo(
    () =>
      Object.values(vendorTypeEnum).map((value) => ({
        label: t(`enums.vendorType_${value}`),
        value,
      })),
    [t],
  )

  const clearFilters = () => {
    setStatus('active')
    setVendorTypes([])
    setOrganizations([])
  }

  return (
    <>
      <PageHeader
        title={t('vendorsPage.title')}
        actions={
          <Button
            variant="primary"
            icon={<PlusIcon />}
            onClick={() => setIsCreateModalOpen(true)}
            data-testid="create-vendor-button"
          >
            {t('vendorsPage.createVendor')}
          </Button>
        }
      >
        <Input
          placeholder={t('vendorsPage.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="vendor-search"
        />
      </PageHeader>
      <MainContent>
        <FilterPane title={t('common.filters')} onClear={clearFilters}>
          <h3 className="mb-2 mt-4 text-base font-semibold">
            {t('vendorsPage.tableHeaderStatus')}
          </h3>
          <SegmentedControl<VendorStatusFilter>
            aria-label={t('vendorsPage.tableHeaderStatus')}
            value={status}
            onChange={setStatus}
            size="sm"
            data-testid="vendor-filter-status"
            options={[
              { value: 'active', label: t('common.active') },
              { value: 'inactive', label: t('common.inactive') },
              { value: 'all', label: t('common.all') },
            ]}
          />
          <h3 className="mb-2 mt-4 text-base font-semibold">{t('vendorsPage.tableHeaderType')}</h3>
          <CustomSelect
            options={vendorTypeOpts}
            values={vendorTypes}
            onChange={setVendorTypes}
            multi
            clearable
          />
          <h3 className="mb-2 mt-4 text-base font-semibold">{t('vendorsPage.organization')}</h3>
          <CustomSelect
            searchable
            options={orgOpts}
            values={organizations}
            onChange={setOrganizations}
            multi
            clearable
            loading={loadingOrgs}
            searchFn={({ state }) => {
              setOrgSearch(state.search)
              return orgOpts
            }}
          />
        </FilterPane>
        <ContentPane>
          <ListToolbar count={data?.count} viewMode={viewMode} onViewModeChange={setViewMode} />
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : vendors.length === 0 ? (
            <EmptyState
              title={t('vendorsPage.emptyState')}
              description={!search ? t('vendorsPage.emptyStateAdd') : undefined}
              className="py-12"
              assistantHint={
                !search && <AssistantHint id="vendors-empty" text={t('assistantHint.vendors')} />
              }
              data-testid="empty-state"
            />
          ) : (
            <>
              {viewMode === 'list' && <VendorListHeader />}

              <ItemCollection viewMode={viewMode} className="mt-2">
                {vendors.map((vendor: Vendor) => (
                  <VendorItem
                    key={vendor.id}
                    vendor={vendor}
                    viewMode={viewMode}
                    onEdit={setEditingVendorId}
                    onToggleActive={(id, isActive) => toggleActiveMutation.mutate({ id, isActive })}
                    isTogglePending={toggleActiveMutation.isPending}
                  />
                ))}
              </ItemCollection>

              <PaginationComponent
                pagination={pagination}
                isLoading={isLoading}
                onNextPage={goToNextPage}
                onPrevPage={goToPrevPage}
                onGoToPage={goToPage}
                className="mt-4"
              />
            </>
          )}
        </ContentPane>
      </MainContent>

      <CreateVendorModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: vendorQueries.all() })
        }}
      />

      {editingVendorId && (
        <EditVendorModal
          isOpen={true}
          onClose={() => setEditingVendorId(null)}
          vendorId={editingVendorId}
        />
      )}
    </>
  )
}
